import { CdnContext } from '@/contexts/CdnContext';
import { ChatRoomUiContext } from '@/contexts/ChatRoomUiContext';
import { CommonConfigContext } from '@/contexts/ConfigContext';
import { GlobalDataContext } from '@/contexts/GlobalDataContext';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { getMainGiftState, getMainUserState } from '../modules/main/selector';
import { LocalStorageKey } from './storage/useLocalStorage';
import { useGameFeature } from './useGameFeature';
import { GiftInfo } from './useGift';
import { useCurrentTime } from './useCurrentTime';

export type ChatInfo = {
    message: string;
    self?: boolean;
    key?: string;
    error?: boolean;
};

const useChatRoom = () => {
    const { Username, LoginToken, PlayerID, IsTrial, IsInternalTest } =
        useSelector(getMainUserState);
    const { chatUrl } = useContext(CdnContext);
    const { isChatRoomOn } = useGameFeature();
    const { lobbyGiftEnable } = useSelector(getMainGiftState);
    const { chatLibReady } = useContext(GlobalDataContext);
    const { isChatRoomEnabled } = useContext(ChatRoomUiContext);
    const MC = useRef<MatrixChat.ChatClient>();
    const [placeholder, setPlaceholder] = useState('');
    const [showChatPanel, setShowChatPanel] = useState(false);
    const [disable, setDisable] = useState(false);
    const displayTimerRef = useRef<NodeJS.Timeout>();
    const keepAliveRef = useRef<NodeJS.Timeout>();
    const { chat: chatConfig } = useContext(CommonConfigContext);
    const msgList = useRef<Array<ChatInfo>>([]);
    const [msgDisplay, setMsgDisplay] = useState<Array<ChatInfo>>([]);
    const [displayLength, setDisplayLength] = useState<number>(20);
    const [intervalTime, setIntervalTime] = useState<number>(1000);
    const [changeNum, setChangeNum] = useState<number>(0);
    const [allowSendMsg, setAllowSendMsg] = useState<boolean>(false);
    const [loginReady, setLoginReady] = useState<boolean>(false);
    const [pendingGifts, setPendingGifts] = useState<Array<GiftInfo>>([]);
    const [giftsDisplay, setGiftsDisplay] = useState<Array<GiftInfo>>([]);
    const giftLoginTS = useRef<number>();
    const { currentTimeStamp } = useCurrentTime();
    const { t } = useTranslation();
    useEffect(() => {
        if (chatConfig) {
            setDisplayLength(chatConfig.displayLength ?? 20);
            setIntervalTime(chatConfig.intervalTime ?? 1000);
        }

        if (IsTrial) {
            setDisable(true);
            setPlaceholder(t('trial_account'));
        } else {
            setDisable(false);
            setPlaceholder(t('chat_room.chat_room_prompt'));
        }
    }, [IsTrial]);

    /**
     * chatEnabled: host list
     * isChatRoomOn: Game Feature
     * isChatRoomEnabled: side menu switch
     * */
    useEffect(() => {
        setShowChatPanel(isChatRoomOn == true && isChatRoomEnabled);
    }, [isChatRoomOn, isChatRoomEnabled]);

    useEffect(() => {
        if (MC.current) {
            MC.current.SetToken(LoginToken.slice(0, 16));
        }
    }, [LoginToken]);

    useEffect(() => {
        if (
            chatLibReady &&
            isChatRoomOn != undefined &&
            lobbyGiftEnable != undefined &&
            (isChatRoomOn || lobbyGiftEnable) &&
            chatUrl &&
            Username &&
            LoginToken &&
            PlayerID
        ) {
            console.log('check =========== chat login');
            MC.current = new MatrixChat.ChatClient(
                chatUrl, // 'http://52.221.102.37:8001',
                PlayerID,
                Username.replace('@', '_'),
                LoginToken.slice(0, 16),
                false
            );
            // MatrixChat.ChatClient.SetSyncTimeout(30000);
            MC.current.Once(MatrixChat.Event.ChatEvent.Ready, () => {
                console.log('check =========== Ready');
                setLoginReady(true);
                setAllowSendMsg(true);
            });
            MC.current.On(MatrixChat.Event.ChatEvent.Message, event => {
                console.log('check ======= chat event', event);
                if (event.RoomName.endsWith('Chat')) {
                    const isSelf = Username.startsWith(
                        event.Sender.replace('_', '@')
                    );
                    if (!event.MsgBanned) {
                        const info = {
                            message: `[${isSelf ? t('system.you').toUpperCase() : shortName(event.Sender.split('_')[0])}] ${event.Msg}`,
                            self: Username.startsWith(event.Sender),
                            key: uuidv4(),
                        };
                        addMessage(info);
                    } else if (isSelf) {
                        addErrorMessage(t('chat_room.inappropriate'));
                    }
                } else if (event.RoomName.endsWith('Gift')) {
                    giftEvent(event);
                }
            });
            // MC.current.On(MatrixChat.Event.ChatEvent.History, event => {
            //      console.log('check ===== History', event);
            // });
            // MC.current.On(MatrixChat.Event.ChatEvent.Forbidden, () => {
            //     console.log('check ===== Forbidden');
            // });
            MC.current.On(MatrixChat.Event.ChatEvent.SyncState, () => {
                setAllowSendMsg(true);
                keepAliveRef.current && clearTimeout(keepAliveRef.current);
                keepAliveRef.current = setTimeout(
                    () => {
                        setAllowSendMsg(false);
                    },
                    60 * 1000 * 3
                );
            });

            return () => MC.current && MC.current.RemoveAllListeners();
        }
    }, [chatUrl, lobbyGiftEnable, isChatRoomOn, chatLibReady]);

    const onFocus = () => {
        // const ts = localStorage.getItem(LocalStorageKey.LastBetTs);
        // if (!IsTrial && Number(ts) + 5 * 60 * 1000 >= currentTimeStamp()-8*3600*1000) {
        //     setDisable(false);
        // } else {
        //     if (IsTrial) {
        //         addErrorMessage(t('trial_account'));
        //     } else {
        //         addErrorMessage(t('chat_room.place_bet'));
        //     }
        //     setDisable(true);
        // }
    };

    const giftEvent = (event: { Msg: string; SendTime: number }) => {
        const giftJson = JSON.parse(event.Msg);
        if (giftLoginTS.current && event.SendTime >= giftLoginTS.current) {
            if (IsInternalTest || (!IsInternalTest && giftJson.gift_id > 0)) {
                const gift = {
                    giftId: giftJson.gift_id,
                    hostId: giftJson.host_id,
                    self: giftJson.player_id == PlayerID.toString(),
                    key: uuidv4(),
                };
                if (giftJson.player_id == PlayerID.toString()) {
                    setGiftsDisplay(list => list?.concat(gift));
                } else {
                    setPendingGifts(list => list?.concat(gift));
                }
            }
        }
    };

    useEffect(() => {
        if (giftsDisplay.length < 10 && pendingGifts.length > 0) {
            let newPendingGifts = pendingGifts.concat([]);
            const gift = newPendingGifts.shift();
            if (gift) {
                setGiftsDisplay(list => list.concat(gift));
                setPendingGifts(newPendingGifts);
            }
        }
    }, [pendingGifts, giftsDisplay]);

    const joinRoom = (gameType: number, hostId: number) => {
        msgList.current = [];
        setMsgDisplay([]);
        setGiftsDisplay([]);
        setPendingGifts([]);
        giftLoginTS.current = currentTimeStamp() - 8 * 3600 * 1000;
        MC.current?.JoinRoom(gameType.toString(), hostId.toString());
    };

    const LeaveRoom = (hostId: number) => {
        MC.current?.LeaveRoom(hostId.toString());
    };

    const sendMessage = (message: string) => {
        if (!message) {
            return false;
        } else if (IsTrial) {
            addErrorMessage(t('trial_account'));
            return false;
        } else {
            const ts = localStorage.getItem(LocalStorageKey.LastBetTs);
            if (Number(ts) + 5 * 60 * 1000 < Date.now()) {
                addErrorMessage(t('chat_room.place_bet'));
                return false;
            }
        }
        if (MC.current) MC.current.Send(message);
        return true;
    };

    const shortName = (name: string) => {
        const pattern = /@.*/;
        const tmp = name.replace(pattern, '');
        return '*' + tmp.slice(-3);
    };

    const removeMsg = (key?: string) => {
        setMsgDisplay(list => list?.filter(g => g.key != key));
    };

    const addErrorMessage = (errorMsg: string) => {
        let newMsg = {
            message: `[${t('system.you').toUpperCase()}] ${errorMsg}`,
            key: uuidv4(),
            self: true,
            error: true,
        };
        msgList.current = [...msgList.current, newMsg];
        setChangeNum(n => n + 1);
    };

    const addMessage = (chatInfo: ChatInfo) => {
        msgList.current = [...msgList.current, chatInfo];
        setChangeNum(n => n + 1);
    };

    const addMsgToDisplay = () => {
        if (msgList.current.length > 0) {
            const item = msgList.current.shift();
            item && setMsgDisplay(displayList => [item, ...displayList]);
            if (displayTimerRef.current) {
                clearTimeout(displayTimerRef.current);
            }
            displayTimerRef.current = setTimeout(() => {
                addMsgToDisplay();
            }, intervalTime);
        } else {
            if (displayTimerRef.current) {
                clearTimeout(displayTimerRef.current);
                displayTimerRef.current = undefined;
            }
        }
    };

    useEffect(() => {
        if (
            msgList &&
            msgList.current.length > 0 &&
            msgDisplay.length < displayLength
        ) {
            addMsgToDisplay();
        }
    }, [changeNum]);

    return {
        joinRoom,
        LeaveRoom,
        disable,
        chatConfig,
        shortName,
        sendMessage,
        placeholder,
        msgDisplay,
        removeMsg,
        onFocus,
        showChatPanel,
        addErrorMessage,
        allowSendMsg,
        pendingGifts,
        setPendingGifts,
        giftsDisplay,
        setGiftsDisplay,
        loginReady,
    };
};
export default useChatRoom;
export type useChatRoomState = ReturnType<typeof useChatRoom>;
